import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Star, Globe, Phone, ExternalLink, Loader2, Bookmark, FlaskConical, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, fmtDate } from "../lib/api";
import { RTYPES } from "../lib/constants";
import { ResearchBadge } from "../components/Badges";
import { PolledProgress } from "../components/ResearchProgress";

const BATCH = 5;

const ModeToggle = ({ mode, setMode }) => (
  <div className="inline-flex rounded-md border bg-slate-50 p-1" data-testid="search-mode-toggle">
    {[["area", "Associated with city / ZIP"], ["radius", "Within radius of a center"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => setMode(k)} data-testid={`search-mode-${k}`}
        className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${mode === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
        {l}
      </button>
    ))}
  </div>
);

const ResultRow = ({ r, selected, toggle, onSave, onResearch, busy }) => (
  <div className={`fade-up grid gap-3 rounded-lg border bg-white p-4 shadow-sm transition-colors md:grid-cols-[auto,1fr,auto] ${selected ? "border-orange-400 ring-1 ring-orange-200" : ""}`} data-testid={`result-row-${r.place_id}`}>
    <Checkbox checked={selected} onCheckedChange={toggle} disabled={!r.accessible} data-testid={`result-select-${r.place_id}`} className="mt-1" />
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900" data-testid="result-name">{r.name}</span>
        {r.saved && <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-700" data-testid="result-saved-indicator">Saved</span>}
        <ResearchBadge status={r.research_status} testId="result-research-status" />
        {r.address_has_zip === false && <span className="text-[10px] font-semibold text-slate-500">ZIP not in address</span>}
        {r.distance_miles != null && <span className="font-mono text-[11px] text-slate-500">{r.distance_miles} mi</span>}
        {r.possible_duplicate_of && <span className="text-[10px] font-semibold text-yellow-700">Possible duplicate listing</span>}
      </div>
      <div className="mt-1 flex items-start gap-1 text-sm text-slate-600"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{r.address}</div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <span className="flex items-center gap-1" data-testid="result-rating"><Star className="h-3.5 w-3.5 text-orange-500" />{r.rating ?? "—"} ({r.review_count?.toLocaleString() ?? 0} reviews)</span>
        {r.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{r.phone}</span>}
        {r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="flex max-w-[260px] items-center gap-1 truncate text-slate-700 underline-offset-2 hover:underline"><Globe className="h-3.5 w-3.5 shrink-0" />{r.website.replace(/^https?:\/\/(www\.)?/, "")}</a> : <span className="text-slate-400">No website listed</span>}
        {r.google_maps_uri && <a href={r.google_maps_uri} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:underline" data-testid="result-google-link"><ExternalLink className="h-3.5 w-3.5" />Google Maps</a>}
        {r.primary_type && <span className="text-slate-400">{r.primary_type}</span>}
        {r.last_researched_at && <span className="text-slate-500">Last researched {fmtDate(r.last_researched_at)}</span>}
        {!r.accessible && <span className="font-semibold text-slate-500">Assigned to {r.assigned_to_name}</span>}
      </div>
    </div>
    <div className="flex flex-wrap items-start gap-2 md:flex-col md:items-stretch">
      {r.location_id && r.accessible && r.last_researched_at ? (
        <Button asChild size="sm" variant="outline" data-testid={`result-open-report-${r.place_id}`}><Link to={`/leads/${r.location_id}`}>Open report</Link></Button>
      ) : null}
      <Button size="sm" disabled={!r.accessible || busy} onClick={onResearch} className="bg-orange-600 hover:bg-orange-700" data-testid={`result-research-btn-${r.place_id}`}>
        <FlaskConical className="mr-1 h-3.5 w-3.5" />Research & Generate Report
      </Button>
      <Button size="sm" variant="outline" disabled={!r.accessible || r.saved} onClick={onSave} data-testid={`result-save-btn-${r.place_id}`}>
        <Bookmark className="mr-1 h-3.5 w-3.5" />{r.saved ? "Saved" : "Save Lead"}
      </Button>
    </div>
  </div>
);

export default function FindRestaurants() {
  const [form, setForm] = useState({ mode: "area", location: "", cuisine: "", rtype: "full_service", radius_miles: 5 });
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.filter((j) => ["queued", "running"].includes(j.status)))).catch(() => {});
  }, []);

  const run = async (pageToken) => {
    if (!form.location.trim()) return toast.error("Enter a city and state or a ZIP code");
    setLoading(true);
    try {
      const body = { ...form, page_token: pageToken || null, center: pageToken ? meta?.center : null };
      const { data } = await api.post("/discover", body);
      setResults((prev) => {
        const base = pageToken ? prev : [];
        const ids = new Set(base.map((x) => x.place_id));
        return [...base, ...data.results.filter((x) => !ids.has(x.place_id))];
      });
      if (!pageToken) setSelected([]);
      setMeta(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const patchResult = (pid, upd) => setResults((rs) => rs.map((x) => (x.place_id === pid ? { ...x, ...upd } : x)));
  const save = async (r) => {
    try {
      const { data } = await api.post("/locations/save", { place: r });
      patchResult(r.place_id, { saved: true, location_id: data.id });
      toast.success(`${r.name} saved with its Google listing data`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const research = async (places) => {
    setBusy(true);
    try {
      const { data } = await api.post("/research", { places });
      data.jobs.forEach((j) => patchResult(places.find((p) => p.name === j.location_name)?.place_id, { saved: true, research_status: "queued", location_id: j.location_id }));
      setJobs((prev) => [...data.jobs, ...prev.filter((p) => !data.jobs.some((j) => j.id === p.id))]);
      setSelected([]);
      toast.success(`Research started for ${data.jobs.length} restaurant(s)`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (pid) => setSelected((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : s.length >= BATCH ? (toast.error(`Batch research is limited to ${BATCH} restaurants`), s) : [...s, pid]));
  const onFinish = (j) => patchResult(results.find((r) => r.location_id === j.location_id)?.place_id, { research_status: j.status, last_researched_at: new Date().toISOString() });

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Discovery</div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Find restaurants</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">Search Google Places, select up to {BATCH} restaurants and run automatic public research. Listing data (rating, reviews, website, phone, address) transfers into the lead and report automatically.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="rounded-lg border bg-white p-5 shadow-sm" data-testid="search-form">
        <ModeToggle mode={form.mode} setMode={(m) => set("mode", m)} />
        <p className="mt-2 text-xs text-slate-500" data-testid="search-mode-explainer">
          {form.mode === "area" ? "Returns restaurants Google associates with the city or ZIP text. This is not a strict ZIP boundary." : "Ranks results near the geographic center of the location, then hides results farther than the radius (computed distance)."}
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-12">
          <div className="space-y-1.5 md:col-span-4">
            <Label>{form.mode === "area" ? "City, State or US ZIP" : "Center (city, ZIP or address)"}</Label>
            <Input placeholder="e.g. 77441 or Fulshear, TX" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="search-location-input" />
          </div>
          <div className="space-y-1.5 md:col-span-3">
            <Label>Cuisine (optional)</Label>
            <Input placeholder="e.g. seafood, Mexican" value={form.cuisine} onChange={(e) => set("cuisine", e.target.value)} data-testid="search-cuisine-input" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Restaurant type</Label>
            <Select value={form.rtype} onValueChange={(v) => set("rtype", v)}>
              <SelectTrigger data-testid="search-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RTYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`search-type-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.mode === "radius" && (
            <div className="space-y-1.5 md:col-span-1">
              <Label>Radius (mi)</Label>
              <Input type="number" min={1} max={31} value={form.radius_miles} onChange={(e) => set("radius_miles", Number(e.target.value))} data-testid="search-radius-input" />
            </div>
          )}
          <div className="flex items-end md:col-span-2">
            <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="search-submit-btn">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}Search
            </Button>
          </div>
        </div>
      </form>

      {jobs.length > 0 && (
        <section data-testid="active-jobs-section">
          <h2 className="mb-3 text-lg font-semibold">Research in progress</h2>
          <div className="grid gap-4 md:grid-cols-2">{jobs.map((j) => <PolledProgress key={j.id} job={j} onFinish={onFinish} compact />)}</div>
        </section>
      )}

      {meta && (
        <section className="space-y-3" data-testid="results-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold" data-testid="results-count">{results.length} restaurants shown{meta.next_page_token ? " · more available" : ""}</h2>
              <p className="flex items-start gap-1 text-xs text-slate-500" data-testid="results-note"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{meta.note}</p>
            </div>
            <Button disabled={!selected.length || busy} onClick={() => research(results.filter((r) => selected.includes(r.place_id)))} className="bg-slate-900 hover:bg-slate-800" data-testid="batch-research-btn">
              <FlaskConical className="mr-2 h-4 w-4" />Research selected ({selected.length}/{BATCH})
            </Button>
          </div>
          {results.length === 0 && <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500" data-testid="results-empty">No restaurants returned for this search.</div>}
          {results.map((r) => (
            <ResultRow key={r.place_id} r={r} busy={busy} selected={selected.includes(r.place_id)} toggle={() => toggle(r.place_id)} onSave={() => save(r)} onResearch={() => research([r])} />
          ))}
          {meta.next_page_token && (
            <Button variant="outline" onClick={() => run(meta.next_page_token)} disabled={loading} className="w-full" data-testid="load-more-btn">
              {loading ? "Loading…" : "Load more results from Google"}
            </Button>
          )}
          {!meta.next_page_token && results.length > 0 && <p className="text-center text-xs text-slate-500" data-testid="results-end-note">The provider returned no further pages for this query. This is still not a guaranteed complete list of every restaurant in the area.</p>}
          <p className="text-right text-[11px] text-slate-400">Listing data © Google Maps</p>
        </section>
      )}
    </div>
  );
}
